import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { I18nProvider as AriaI18nProvider } from 'react-aria-components';
import type { Locale } from '../api/types';
import { formatCalendarDate, formatTimestamp } from '../lib/dates';
import { formatDecimal, type DecimalFormatOptions } from '../lib/decimal';
import { PREFERENCE_KEYS, readPreference, writePreference } from '../lib/storage';
import { ar } from './ar';
import { en, type Dictionary } from './en';

const DICTIONARIES: Record<Locale, Dictionary> = { ar, en };

/**
 * Formatting locales. Arabic uses Western digits by default (accounting readability: amounts
 * match ETA documents and spreadsheets); English uses day-first dates as is usual in Egypt.
 */
export const FORMAT_LOCALE: Record<Locale, string> = { ar: 'ar-EG-u-nu-latn', en: 'en-GB' };
export const DIRECTION: Record<Locale, 'rtl' | 'ltr'> = { ar: 'rtl', en: 'ltr' };

export function isLocale(value: unknown): value is Locale {
  return value === 'ar' || value === 'en';
}

/** Saved choice, else Arabic (the product is RTL first). */
export function initialLocale(): Locale {
  const stored = readPreference(PREFERENCE_KEYS.locale);
  return isLocale(stored) ? stored : 'ar';
}

/** <html lang dir> follow the locale, so the whole document (and the browser) flips. */
export function applyDocumentLocale(locale: Locale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = DIRECTION[locale];
}

export interface I18n {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  t: Dictionary;
  formatLocale: string;
  setLocale: (locale: Locale) => void;
  /** Money: at least 2 and at most 5 decimals, so server values are shown without rounding loss. */
  formatAmount: (value: string, currency?: string) => string;
  formatNumber: (value: string, options?: DecimalFormatOptions) => string;
  /** Rates stored as percentages ("14.00000" → "14%"). */
  formatPercent: (value: string) => string;
  formatDate: (value: string) => string;
  formatDateTime: (value: string) => string;
  /** Picks the Arabic or English variant of a bilingual reference value. */
  pick: (arabic: string | null | undefined, english: string | null | undefined) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** Tests and stories pin a locale; the app reads the saved preference. */
  initial?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => initial ?? initialLocale());

  useLayoutEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    writePreference(PREFERENCE_KEYS.locale, next);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18n>(() => {
    const formatLocale = FORMAT_LOCALE[locale];
    return {
      locale,
      dir: DIRECTION[locale],
      t: DICTIONARIES[locale],
      formatLocale,
      setLocale,
      formatAmount: (amount, currency) =>
        formatDecimal(amount, formatLocale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
          ...(currency ? { currency, currencyDisplay: 'code' as const } : {}),
        }),
      formatNumber: (number, options) => formatDecimal(number, formatLocale, options),
      formatPercent: (rate) =>
        formatDecimal(rate, formatLocale, { style: 'percent', maximumFractionDigits: 5 }),
      formatDate: (date) => formatCalendarDate(date, formatLocale),
      formatDateTime: (timestamp) => formatTimestamp(timestamp, formatLocale),
      pick: (arabic, english) => (locale === 'ar' ? arabic || english : english || arabic) || '',
    };
  }, [locale, setLocale]);

  return (
    <I18nContext.Provider value={value}>
      <AriaI18nProvider locale={FORMAT_LOCALE[locale]}>{children}</AriaI18nProvider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n() must be used inside <I18nProvider>');
  return value;
}
